/**
 * The Governance Queue — the TypeScript mirror of
 * Lighthouse.Api Contracts/Models/ObservatoryGovernanceModels.cs, served by
 * `GET /api/observatory/governance[?finding=]` (ObservatoryGovernanceController,
 * LighthouseAdmin at class level, GET only, no ReportCaller).
 *
 * camelCase, as the API serialises. Every enum-like value arrives as its C# NAME
 * (the controller writes `ToString()` / `nameof`), and every C# nullable is
 * `| null` — the API writes nulls explicitly. Compare with `== null`.
 *
 * NULL IS NOT EMPTY, and the whole endpoint is built on that. A null finding list
 * was NOT COMPUTED (another group was asked for, or — for the inactivity list —
 * the run log cannot vouch for the window); an empty list was computed and found
 * nothing. A page that renders both as "none" has turned an unasked question into
 * a clean bill of health. A null renders NOT_MEASURED (utils/format.ts).
 *
 * TIMESTAMPS: every instant here is a C# `DateTime` written as UTC, which SQL
 * Server hands back with no kind — so the wire value can carry NO offset suffix
 * (`2026-09-01T12:00:00`), and `Date.parse` reads that as LOCAL time. Read them
 * through `utcIso` (components/governance/governanceFormat.ts).
 *
 * This module imports nothing, so it stays safe to import under vitest.
 */

/**
 * The seven groups the controller accepts in `?finding=` (ObservatoryGovernanceController
 * NormaliseFinding). Anything else is a 400 "Unknown finding", never an ignored filter.
 */
export type GovernanceFinding =
  | 'ownership' | 'expiring' | 'lapsed' | 'archived' | 'reach' | 'schedules' | 'inactive'

/**
 * Every finding, in the order the page and the preview present them — weakest
 * (inactive) last, and grants on archived reports straight after the lapsed ones,
 * because both are grant rows that are NOT working access.
 */
export const GOVERNANCE_FINDINGS: readonly GovernanceFinding[] = [
  'ownership', 'expiring', 'lapsed', 'archived', 'reach', 'schedules', 'inactive',
]

/** Which mechanism a grant row is. Never totalled together without saying so. */
export type GovernanceGrantKind = 'ReportPermission' | 'WorkspaceMembership'

/** `User`, `Group` or `Role`. In practice always `User` (the only writer rejects the others). */
export type GovernancePrincipalType = 'User' | 'Group' | 'Role'

/** What a grant confers. Not a hierarchy, and never Owner or Maintainer — those are columns. */
export type GovernanceGrantRole = 'Viewer' | 'Distributor' | 'Contributor'

/** Why a schedule was listed. Never empty for a served row. */
export type ScheduleIdentityReason = 'RunAsDiffersFromOwner' | 'NoBusinessOwner' | 'ReportNeedsAttention'

/** One report flagged AttentionRequired by the orphan sweep — read, never re-derived. */
export interface OwnershipGap {
  reportId: string
  name: string
  /** Always `AttentionRequired`: the query selects on nothing else. */
  status: string
  /** Null for a report filed in none — which IS how a personal report is stored. */
  workspaceId: string | null
  workspaceName: string | null
  /** False for an unfiled personal report, by design: it has no workspace at all. */
  isPersonalWorkspace: boolean
  /** THE FINDING when null: nobody is accountable for what the report means. */
  businessOwnerPrincipalId: string | null
  maintainerPrincipalId: string | null
  /** History, not accountability. Never render under an "Owner" heading. */
  authorUserId: string | null
  /** The creator's name as stamped at write time; null when none was. */
  creatorSnapshot: string | null
  /** Null when the flag predates the column being written. */
  attentionRequiredAt: string | null
  attentionReason: string | null
  /** From SavedReport.LastRunAt, which survives every prune. Null means never. */
  lastRunAt: string | null
  /** A measured count — schedules are rows. Also the server's ordering key. */
  activeScheduleCount: number
}

/** One grant carrying an expiry — nearing it, or already past it. */
export interface GrantExpiry {
  grantKind: GovernanceGrantKind
  principalId: string
  principalDisplayName: string | null
  principalType: GovernancePrincipalType
  role: GovernanceGrantRole
  grantedAt: string
  /** Who made it (GrantedBy for a permission, CreatedBy for a membership). Null when unrecorded. */
  grantedBy: string | null
  grantedByDisplayName: string | null
  grantNote: string | null
  /** Never null in these lists: both select on it being set. */
  expiresAt: string
  /**
   * Whole days, signed and TRUNCATED (controller WholeDaysUntil): a grant that lapsed
   * five hours ago reads 0, not -1. Never render a lapse from this — use `expiresAt`.
   */
  daysUntilExpiry: number
  /** Null for a workspace membership. */
  reportId: string | null
  /** Null for a membership, or (should not happen) an unresolved report. */
  reportName: string | null
  /**
   * The report's lifecycle status by name — null for a membership or an unresolved
   * report. Never `Archived`: those grants are listed apart (`grantsOnArchivedReports`).
   * A Delisted, Retired or Deleted report's grant is marked with it, never hidden.
   */
  reportStatus: string | null
  workspaceId: string | null
  workspaceName: string | null
  isPersonalWorkspace: boolean
}

/** One report and how many principals hold a standing claim on it. Three counts, never summed. */
export interface ReportReach {
  reportId: string
  name: string
  status: string
  workspaceId: string | null
  workspaceName: string | null
  isPersonalWorkspace: boolean
  /** Live direct grants to a User — each one a person. (Was `directGrantCount`, every type.) */
  userGrantCount: number
  /**
   * Live direct grants to a Group or Role — GROUP MEMBERS NOT RESOLVABLE. Stored, never
   * expanded, so they reach nobody; zero by construction on a database only
   * Cartographer writes (see `groupOrRoleGrantsCaption`).
   */
  groupOrRoleGrantCount: number
  /** Live memberships of its workspace, EXCLUDING the owner's own. Zero when filed in none. */
  workspaceMemberCount: number
}

/**
 * One direct grant over an ARCHIVED report — listed apart, never counted as live,
 * never hidden.
 *
 * NOT LATENT: an archived report keeps CanView for its grantees, definition
 * included (ReportAccessService; GET reporting/reports/{id}), so this is view-only
 * access that exists now. Run, export and schedule return on restore — unless the
 * grant has lapsed (`isLapsed`), in which case nothing returns. A Group or Role row
 * confers nothing either way.
 */
export interface ArchivedReportGrant {
  reportId: string
  /** Null only if the report could not be resolved. */
  reportName: string | null
  /** Null when the row was archived before Cartographer stamped the column. */
  archivedAt: string | null
  workspaceId: string | null
  workspaceName: string | null
  isPersonalWorkspace: boolean
  principalId: string
  principalDisplayName: string | null
  principalType: GovernancePrincipalType
  role: GovernanceGrantRole
  grantedAt: string
  grantedBy: string | null
  grantedByDisplayName: string | null
  grantNote: string | null
  /** Null means it does not expire. */
  expiresAt: string | null
  /** Already lapsed: confers nothing now, and returns nothing on restore. */
  isLapsed: boolean
}

/** One active schedule delivering under an identity nobody is accountable for. */
export interface ScheduleIdentity {
  scheduleId: string
  scheduleName: string
  reportId: string
  reportName: string
  reportStatus: string
  runAsUserId: string
  runAsDisplayName: string | null
  businessOwnerPrincipalId: string | null
  reasons: ScheduleIdentityReason[]
  cronExpression: string
  timeZoneId: string
  /** Null when the stored list could not be read — "unreadable", which is not zero. */
  recipientCount: number | null
  /** Null when it has never delivered. */
  lastRunAt: string | null
  lastRunStatus: string | null
}

/** One principal holding live access who ran nothing inside a window the log can vouch for. */
export interface InactiveGrantHolder {
  principalId: string
  displayName: string | null
  /** Live grants on reports that can still RUN (Active, AttentionRequired, Delisted). */
  reportGrantCount: number
  /**
   * Live grants on reports that can NOT run (archived, retired, deleted) — stale
   * view access, counted apart and never as live. A holder with only these is still
   * listed, with `reportGrantCount` 0.
   */
  notRunnableReportGrantCount: number
  workspaceMembershipCount: number
}

/** The whole response. Under `?finding=` every other group's list AND count is null. */
export interface GovernanceOverview {
  /** The group asked for, lower-case, or null when everything was. */
  finding: GovernanceFinding | null

  ownershipGaps: OwnershipGap[] | null
  ownershipGapCount: number | null

  grantsNearingExpiry: GrantExpiry[] | null
  grantsNearingExpiryCount: number | null

  /** These confer nothing — housekeeping, never exposure. */
  lapsedGrants: GrantExpiry[] | null
  lapsedGrantCount: number | null

  /** Grants over archived reports: view-only now, run/export/schedule on restore. Never live. */
  grantsOnArchivedReports: ArchivedReportGrant[] | null
  /** Every such grant, lapsed ones included. Null exactly when the list is. */
  grantsOnArchivedReportsCount: number | null

  broadlyReachableReports: ReportReach[] | null
  /**
   * Reports carrying any live claim beyond their ownership — a grant of any type, or a
   * non-owner member. Uncapped. Exactly `reportsSharedWithPeopleCount +
   * reportsWithOnlyGroupOrRoleGrantsCount`; its denominator is `reportsExamined`.
   */
  sharedReportCount: number | null
  /** Of those, reports reaching at least one PERSON (a user grant or a non-owner member). */
  reportsSharedWithPeopleCount: number | null
  /** Of those, reports reachable only through group or role grants — which reach nobody. */
  reportsWithOnlyGroupOrRoleGrantsCount: number | null
  /**
   * What every group-or-role figure means, served verbatim so the page cannot soften
   * it. Starts "group members not resolvable". Served whatever was asked for.
   */
  groupOrRoleGrantsCaption: string

  schedulesRunningAsNonOwner: ScheduleIdentity[] | null
  schedulesRunningAsNonOwnerCount: number | null

  /**
   * Null whenever the run log cannot cover the window — "we cannot tell", not "nobody". Lists live holders first, then
   * those whose only grants are on reports that cannot run.
   */
  inactiveGrantHolders: InactiveGrantHolder[] | null
  /** People holding LIVE access who ran nothing. Never includes a view-only-only holder (D7(b)). */
  inactiveGrantHolderCount: number | null
  /**
   * Holders whose ONLY grants are on reports that cannot run — listed, never counted as live. Absent from an older
   * API, which counted them in inactiveGrantHolderCount.
   */
  inactiveViewOnlyHolderCount?: number | null

  /** Every Active and AttentionRequired report. */
  reportsExamined: number
  /** Every direct grant row, live and lapsed. */
  reportGrantsExamined: number
  /** Every workspace membership row, live and lapsed. */
  workspaceMembershipsExamined: number
  expiringWithinDays: number
  inactivityThresholdDays: number
  /** The oldest run on file; null when nothing has ever run. */
  runLogReachesBackTo: string | null
  runLogCoversInactivityWindow: boolean
  /** The cap on every list; the count beside each list is the measurement. */
  maxRowsPerFinding: number
}
